import type { ReactNode, RefObject } from 'react';

import { useCondensedTickerSummary } from '#/components/detail/useCondensedTickerSummary';
import { SectionNav } from '#/components/ui/SectionNav';
import type { StickySectionEntry } from '#/components/ui/StickySectionNav';

interface DetailSectionNavProps {
  sections: StickySectionEntry[];
  /** Attached by the page to the content column the nav scrolls and spies on. */
  contentRef: RefObject<HTMLDivElement | null>;
  /**
   * Attached by the page to the in-place price summary; its scroll position
   * gates `condensedSummary`.
   */
  priceSummaryRef: RefObject<HTMLElement | null>;
  /**
   * Mobile condensed summary row, docked into the nav once the in-place
   * summary scrolls beneath the sticky stack.
   */
  condensedSummary?: ReactNode;
  /**
   * Optional GTM annotation forwarded to the section jump buttons. Omitted
   * leaves them to generic auto-capture.
   */
  jumpTrack?: { track: string; context: string };
}

/**
 * The detail-page section nav: the shared SectionNav plus the scroll-driven
 * condensed-summary docking that only detail pages have. The docking hook
 * lives here rather than in the page so its toggle — which fires throughout
 * scrolling — re-renders only this nav subtree, matching SectionNav's
 * scroll-spy state.
 */
export function DetailSectionNav({
  sections,
  contentRef,
  priceSummaryRef,
  condensedSummary,
  jumpTrack,
}: DetailSectionNavProps) {
  const showCondensedSummary = useCondensedTickerSummary(priceSummaryRef);

  return (
    <SectionNav
      sections={sections}
      contentRef={contentRef}
      condensedSummary={showCondensedSummary ? condensedSummary : undefined}
      jumpTrack={jumpTrack}
    />
  );
}
